import type { CurrentAccount } from '../../../types/account';
import { formatEpochDate, remainDaysUntil } from '../../../utils/timeFormat';

export type UserCenterProfile = {
  userName: string;
  phone: string;
  email: string;
  platformEmail: string;
  orgId: string;
  planName: string;
  /** 套餐等级；0 为基础会员。 */
  planGrade: number | null;
  expireDate: string;
  remainDays: number;
  shopUsed: number;
  shopLimit: number;
  emailUsed: number;
  emailLimit: number;
  emailUnlimited?: boolean;
};

export const emptyUserCenterProfile: UserCenterProfile = {
  userName: '',
  phone: '',
  email: '',
  platformEmail: '',
  orgId: '',
  planName: '',
  planGrade: null,
  expireDate: '',
  remainDays: 0,
  shopUsed: 0,
  shopLimit: 0,
  emailUsed: 0,
  emailLimit: 0,
  emailUnlimited: false
};

/** 基础会员：grade 为 0，或名称含「基础会员」。 */
export function isBasicMembership(profile: Pick<UserCenterProfile, 'planGrade' | 'planName'>) {
  if (profile.planGrade === 0) return true;
  return profile.planName.includes('基础会员');
}

/** 用 /api/v1/account/current 的 content 生成个人中心展示数据。 */
export function mapAccountToProfile(account: CurrentAccount | null | undefined): UserCenterProfile {
  if (!account) return { ...emptyUserCenterProfile };
  const shop = account.quota?.shop;
  const mail = account.quota?.mail;
  const unlimited = mail?.unlimited === true;
  const grade = account.membership?.grade;
  return {
    userName: account.displayName?.trim() || '',
    phone: account.phone?.trim() || '',
    email: account.email?.trim() || '',
    platformEmail: account.platformEmail?.trim() || '',
    orgId: account.userCode?.trim() || '',
    planName: account.membership?.name?.trim() || '',
    planGrade: typeof grade === 'number' && Number.isFinite(grade) ? grade : null,
    expireDate: formatEpochDate(account.membership?.expiresAt),
    remainDays: remainDaysUntil(account.membership?.expiresAt),
    shopUsed: shop?.used ?? 0,
    shopLimit: shop?.total ?? 0,
    emailUsed: mail?.used ?? 0,
    emailLimit: unlimited ? 0 : mail?.total ?? 0,
    emailUnlimited: unlimited
  };
}

export function maskPhone(phone: string) {
  const text = phone?.trim() || '';
  if (!text) return '—';
  if (text.length < 7) return text;
  return `${text.slice(0, 3)}****${text.slice(-4)}`;
}

export function utf8ByteLength(value: string) {
  return new TextEncoder().encode(value).length;
}

/** 新密码不得包含空白。覆盖 Java 空白和 Unicode 分隔符。 */
export function containsPasswordWhitespace(value: string) {
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (code === 0x09 || code === 0x0a || code === 0x0b || code === 0x0c || code === 0x0d) return true;
    if (code >= 0x1c && code <= 0x20) return true;
    if (code === 0xa0 || code === 0x1680) return true;
    if (code >= 0x2000 && code <= 0x200a) return true;
    if (code === 0x2028 || code === 0x2029 || code === 0x202f || code === 0x205f || code === 0x3000) return true;
  }
  return false;
}

export function getPasswordStrength(password: string): 0 | 1 | 2 | 3 {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score += 1;
  if (/[a-zA-Z]/.test(password) && /\d/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password) || password.length >= 12) score += 1;
  if (score <= 1) return 1;
  if (score === 2) return 2;
  return 3;
}
